import React, { useEffect, useRef, useState } from "react";

type RevealProps = {
  children: React.ReactNode;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
  /** animation name: "fade-up", "fade-left", "fade-right", "zoom" */
  animation?: "fade-up" | "fade-left" | "fade-right" | "zoom" | "fade-in";
  /** alias to support older code that used "variant" prop */
  variant?: "up" | "zoom" | "left" | "right";
  /** delay in ms */
  delay?: number;
  /** animation duration in ms */
  duration?: number;
  /** if true, animate only once */
  once?: boolean;
  /** observer threshold */
  threshold?: number;
};

export default function Reveal({
  children,
  className = "",
  as = "div",
  animation = "fade-up",
  variant,
  delay = 0,
  duration = 700,
  once = false,
  threshold = 0.15,
}: RevealProps) {
  const Comp: any = as;
  const ref = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    setInView(false);

    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting) {
          setInView(true);
          if (once) obs.unobserve(entry.target);
        } else {
          if (!once) setInView(false);
        }
      },
      { threshold }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [once, threshold]);

  const effectiveVariant = variant ? variant : animation === "fade-up" ? "up" : animation === "zoom" ? "zoom" : animation === "fade-left" ? "left" : animation === "fade-right" ? "right" : "up";

  const variantClass =
    effectiveVariant === "up"
      ? "reveal-up"
      : effectiveVariant === "zoom"
      ? "reveal-zoom"
      : effectiveVariant === "left"
      ? "reveal-left"
      : "reveal-right";

  return (
    <>
      <style>{`
        .reveal-base {
          transition-property: opacity, transform, filter;
          transition-duration: var(--reveal-duration, 700ms);
          transition-timing-function: cubic-bezier(0.2, 0.8, 0.2, 1);
          transition-delay: var(--reveal-delay, 0ms);
          will-change: opacity, transform;
        }
        .reveal-out {
          opacity: 0;
          filter: blur(6px);
          pointer-events: none;
        }
        .reveal-in {
          opacity: 1;
          filter: blur(0);
          pointer-events: auto;
        }

        .reveal-up.reveal-out {
          transform: translate3d(0, 18px, 0);
        }
        .reveal-up.reveal-in {
          transform: translate3d(0, 0, 0);
        }

        .reveal-zoom.reveal-out {
          transform: scale(0.97);
        }
        .reveal-zoom.reveal-in {
          transform: scale(1);
        }

        .reveal-left.reveal-out {
          transform: translate3d(-18px, 0, 0);
        }
        .reveal-left.reveal-in {
          transform: translate3d(0, 0, 0);
        }

        .reveal-right.reveal-out {
          transform: translate3d(18px, 0, 0);
        }
        .reveal-right.reveal-in {
          transform: translate3d(0, 0, 0);
        }

        @media (prefers-reduced-motion: reduce) {
          .reveal-base {
            transition: none !important;
          }
          .reveal-out,
          .reveal-in {
            opacity: 1 !important;
            filter: none !important;
            transform: none !important;
            pointer-events: auto !important;
          }
        }
      `}</style>

      <Comp
        ref={ref as any}
        className={[
          "reveal-base",
          variantClass,
          inView ? "reveal-in" : "reveal-out",
          className,
        ].join(" ")}
        style={
          {
            ["--reveal-delay" as any]: `${delay}ms`,
            ["--reveal-duration" as any]: `${duration}ms`,
          } as React.CSSProperties
        }
      >
        {children}
      </Comp>
    </>
  );
}
